import React from 'react';
import {
  Card,
  makeStyles,
  tokens,
  Skeleton,
  SkeletonItem,
  Tooltip
} from '@fluentui/react-components';
import {
  HatGraduation20Regular,
  PersonAccounts20Regular,
  Briefcase20Regular,
  PeopleTeam20Regular,
  PersonSwap20Regular,
  Add16Regular,
  Edit16Regular,
  Delete16Regular,
  CheckmarkCircle16Regular,
  ErrorCircle16Regular,
  Checkmark12Regular,
  Dismiss12Regular
} from '@fluentui/react-icons';
import type { DashboardOperations } from '../../types';

const useStyles = makeStyles({
  wrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '14px',
    marginBottom: '28px',
    '@media (max-width: 1400px)': {
      gridTemplateColumns: 'repeat(3, 1fr)',
    },
    '@media (max-width: 900px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    padding: '14px 17px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    paddingBottom: '10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  cardIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '29px',
    height: '29px',
    borderRadius: '6px',
    flexShrink: 0,
  },
  iconStudents: {
    backgroundColor: tokens.colorPaletteBlueBackground2,
    color: tokens.colorPaletteBlueForeground2,
  },
  iconTeachers: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  iconStaff: {
    backgroundColor: tokens.colorPalettePurpleBackground2,
    color: tokens.colorPalettePurpleForeground2,
  },
  iconClasses: {
    backgroundColor: tokens.colorPaletteMarigoldBackground2,
    color: tokens.colorPaletteMarigoldForeground2,
  },
  iconEnrollments: {
    backgroundColor: tokens.colorPaletteTealBackground2,
    color: tokens.colorPaletteTealForeground2,
  },
  cardTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  statsRows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  statRow: {
    display: 'grid',
    gridTemplateColumns: '22px 60px 1fr',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 7px',
    borderRadius: '5px',
    backgroundColor: tokens.colorNeutralBackground2,
    fontSize: '12px',
  },
  statIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statIconNew: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statIconUpdate: {
    color: tokens.colorBrandForeground1,
  },
  statIconDelete: {
    color: tokens.colorPaletteYellowForeground1,
  },
  statIconSuccess: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statIconError: {
    color: tokens.colorPaletteRedForeground1,
  },
  statLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: '11px',
  },
  statValues: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '5px',
  },
  statTotal: {
    fontWeight: 600,
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    minWidth: '34px',
    textAlign: 'right' as const,
  },
  statBreakdown: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  successCount: {
    color: tokens.colorPaletteGreenForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '1px',
  },
  failedCount: {
    color: tokens.colorPaletteRedForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '1px',
  },
  separator: {
    color: tokens.colorNeutralForeground4,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  totalLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  totalValue: {
    fontSize: '15px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
  },
  skeletonCard: {
    padding: '14px 17px',
  },
});

interface OperationMetricsCardsProps {
  operations: DashboardOperations | null;
  loading?: boolean;
}

export const OperationMetricsCards: React.FC<OperationMetricsCardsProps> = ({ operations, loading }) => {
  const styles = useStyles();

  const formatNumber = (num: number) => {
    return num.toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className={styles.wrapper}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i} className={styles.skeletonCard}>
            <Skeleton>
              <SkeletonItem size={24} style={{ width: '100%', marginBottom: '10px' }} />
              <SkeletonItem size={16} style={{ width: '100%', marginBottom: '4px' }} />
              <SkeletonItem size={16} style={{ width: '100%', marginBottom: '4px' }} />
              <SkeletonItem size={16} style={{ width: '100%', marginBottom: '4px' }} />
              <SkeletonItem size={16} style={{ width: '100%', marginBottom: '4px' }} />
              <SkeletonItem size={16} style={{ width: '100%' }} />
            </Skeleton>
          </Card>
        ))}
      </div>
    );
  }

  // Cards para entidades com New/Update/Delete/Errors/Scheduled
  const entityCards = [
    {
      icon: <HatGraduation20Regular />,
      iconClass: styles.iconStudents,
      title: 'Alunos',
      data: operations?.students,
    },
    {
      icon: <PersonAccounts20Regular />,
      iconClass: styles.iconTeachers,
      title: 'Professores',
      data: operations?.teachers,
    },
    {
      icon: <Briefcase20Regular />,
      iconClass: styles.iconStaff,
      title: 'Staff',
      data: operations?.staff,
    },
  ];

  return (
    <div className={styles.wrapper}>
      {/* Cards de Entidades: Alunos, Professores, Staff */}
      {entityCards.map((card, index) => (
        <Card key={index} className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={`${styles.cardIcon} ${card.iconClass}`}>
              {card.icon}
            </div>
            <span className={styles.cardTitle}>{card.title}</span>
          </div>
          <div className={styles.statsRows}>
            {/* New */}
            <div className={styles.statRow}>
              <span className={`${styles.statIcon} ${styles.statIconNew}`}><Add16Regular /></span>
              <span className={styles.statLabel}>New</span>
              <div className={styles.statValues}>
                <span className={styles.statTotal}>{formatNumber((card.data?.newDetail?.success ?? 0) + (card.data?.newDetail?.failed ?? 0))}</span>
                <Tooltip content={`Sucesso: ${card.data?.newDetail?.success ?? 0} | Falha: ${card.data?.newDetail?.failed ?? 0}`} relationship="label">
                  <span className={styles.statBreakdown}>
                    <span className={styles.successCount}><Checkmark12Regular />{card.data?.newDetail?.success ?? 0}</span>
                    <span className={styles.separator}>/</span>
                    <span className={styles.failedCount}><Dismiss12Regular />{card.data?.newDetail?.failed ?? 0}</span>
                  </span>
                </Tooltip>
              </div>
            </div>
            {/* Update */}
            <div className={styles.statRow}>
              <span className={`${styles.statIcon} ${styles.statIconUpdate}`}><Edit16Regular /></span>
              <span className={styles.statLabel}>Update</span>
              <div className={styles.statValues}>
                <span className={styles.statTotal}>{formatNumber((card.data?.updateDetail?.success ?? 0) + (card.data?.updateDetail?.failed ?? 0))}</span>
                <Tooltip content={`Sucesso: ${card.data?.updateDetail?.success ?? 0} | Falha: ${card.data?.updateDetail?.failed ?? 0}`} relationship="label">
                  <span className={styles.statBreakdown}>
                    <span className={styles.successCount}><Checkmark12Regular />{card.data?.updateDetail?.success ?? 0}</span>
                    <span className={styles.separator}>/</span>
                    <span className={styles.failedCount}><Dismiss12Regular />{card.data?.updateDetail?.failed ?? 0}</span>
                  </span>
                </Tooltip>
              </div>
            </div>
            {/* Delete */}
            <div className={styles.statRow}>
              <span className={`${styles.statIcon} ${styles.statIconDelete}`}><Delete16Regular /></span>
              <span className={styles.statLabel}>Delete</span>
              <div className={styles.statValues}>
                <span className={styles.statTotal}>{formatNumber((card.data?.deleteDetail?.success ?? 0) + (card.data?.deleteDetail?.failed ?? 0))}</span>
                <Tooltip content={`Sucesso: ${card.data?.deleteDetail?.success ?? 0} | Falha: ${card.data?.deleteDetail?.failed ?? 0}`} relationship="label">
                  <span className={styles.statBreakdown}>
                    <span className={styles.successCount}><Checkmark12Regular />{card.data?.deleteDetail?.success ?? 0}</span>
                    <span className={styles.separator}>/</span>
                    <span className={styles.failedCount}><Dismiss12Regular />{card.data?.deleteDetail?.failed ?? 0}</span>
                  </span>
                </Tooltip>
              </div>
            </div>
            {/* Sucesso */}
            <div className={styles.statRow}>
              <span className={`${styles.statIcon} ${styles.statIconSuccess}`}><CheckmarkCircle16Regular /></span>
              <span className={styles.statLabel}>Sucesso</span>
              <div className={styles.statValues}>
                <span className={styles.statTotal}>{formatNumber((card.data?.newDetail?.success ?? 0) + (card.data?.updateDetail?.success ?? 0) + (card.data?.deleteDetail?.success ?? 0))}</span>
              </div>
            </div>
            {/* Erros */}
            <div className={styles.statRow}>
              <span className={`${styles.statIcon} ${styles.statIconError}`}><ErrorCircle16Regular /></span>
              <span className={styles.statLabel}>Erros</span>
              <div className={styles.statValues}>
                <span className={styles.statTotal}>{formatNumber((card.data?.newDetail?.failed ?? 0) + (card.data?.updateDetail?.failed ?? 0) + (card.data?.deleteDetail?.failed ?? 0))}</span>
              </div>
            </div>
          </div>
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>Total</span>
            <span className={styles.totalValue}>{formatNumber(card.data?.total ?? 0)}</span>
          </div>
        </Card>
      ))}

      {/* Card de Classes: New/Delete/Errors/Scheduled */}
      <Card className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={`${styles.cardIcon} ${styles.iconClasses}`}>
            <PeopleTeam20Regular />
          </div>
          <span className={styles.cardTitle}>Classes</span>
        </div>
        <div className={styles.statsRows}>
          {/* New */}
          <div className={styles.statRow}>
            <span className={`${styles.statIcon} ${styles.statIconNew}`}><Add16Regular /></span>
            <span className={styles.statLabel}>New</span>
            <div className={styles.statValues}>
              <span className={styles.statTotal}>{formatNumber((operations?.classes?.newDetail?.success ?? 0) + (operations?.classes?.newDetail?.failed ?? 0))}</span>
              <Tooltip content={`Sucesso: ${operations?.classes?.newDetail?.success ?? 0} | Falha: ${operations?.classes?.newDetail?.failed ?? 0}`} relationship="label">
                <span className={styles.statBreakdown}>
                  <span className={styles.successCount}><Checkmark12Regular />{operations?.classes?.newDetail?.success ?? 0}</span>
                  <span className={styles.separator}>/</span>
                  <span className={styles.failedCount}><Dismiss12Regular />{operations?.classes?.newDetail?.failed ?? 0}</span>
                </span>
              </Tooltip>
            </div>
          </div>
          {/* Update */}
          <div className={styles.statRow}>
            <span className={`${styles.statIcon} ${styles.statIconUpdate}`}><Edit16Regular /></span>
            <span className={styles.statLabel}>Update</span>
            <div className={styles.statValues}>
              <span className={styles.statTotal}>{formatNumber((operations?.classes?.updateDetail?.success ?? 0) + (operations?.classes?.updateDetail?.failed ?? 0))}</span>
              <Tooltip content={`Sucesso: ${operations?.classes?.updateDetail?.success ?? 0} | Falha: ${operations?.classes?.updateDetail?.failed ?? 0}`} relationship="label">
                <span className={styles.statBreakdown}>
                  <span className={styles.successCount}><Checkmark12Regular />{operations?.classes?.updateDetail?.success ?? 0}</span>
                  <span className={styles.separator}>/</span>
                  <span className={styles.failedCount}><Dismiss12Regular />{operations?.classes?.updateDetail?.failed ?? 0}</span>
                </span>
              </Tooltip>
            </div>
          </div>
          {/* Delete */}
          <div className={styles.statRow}>
            <span className={`${styles.statIcon} ${styles.statIconDelete}`}><Delete16Regular /></span>
            <span className={styles.statLabel}>Delete</span>
            <div className={styles.statValues}>
              <span className={styles.statTotal}>{formatNumber((operations?.classes?.deleteDetail?.success ?? 0) + (operations?.classes?.deleteDetail?.failed ?? 0))}</span>
              <Tooltip content={`Sucesso: ${operations?.classes?.deleteDetail?.success ?? 0} | Falha: ${operations?.classes?.deleteDetail?.failed ?? 0}`} relationship="label">
                <span className={styles.statBreakdown}>
                  <span className={styles.successCount}><Checkmark12Regular />{operations?.classes?.deleteDetail?.success ?? 0}</span>
                  <span className={styles.separator}>/</span>
                  <span className={styles.failedCount}><Dismiss12Regular />{operations?.classes?.deleteDetail?.failed ?? 0}</span>
                </span>
              </Tooltip>
            </div>
          </div>
          {/* Sucesso */}
          <div className={styles.statRow}>
            <span className={`${styles.statIcon} ${styles.statIconSuccess}`}><CheckmarkCircle16Regular /></span>
            <span className={styles.statLabel}>Sucesso</span>
            <div className={styles.statValues}>
              <span className={styles.statTotal}>{formatNumber((operations?.classes?.newDetail?.success ?? 0) + (operations?.classes?.updateDetail?.success ?? 0) + (operations?.classes?.deleteDetail?.success ?? 0))}</span>
            </div>
          </div>
          {/* Erros */}
          <div className={styles.statRow}>
            <span className={`${styles.statIcon} ${styles.statIconError}`}><ErrorCircle16Regular /></span>
            <span className={styles.statLabel}>Erros</span>
            <div className={styles.statValues}>
              <span className={styles.statTotal}>{formatNumber((operations?.classes?.newDetail?.failed ?? 0) + (operations?.classes?.updateDetail?.failed ?? 0) + (operations?.classes?.deleteDetail?.failed ?? 0))}</span>
            </div>
          </div>
        </div>
        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>Total</span>
          <span className={styles.totalValue}>{formatNumber(operations?.classes?.total ?? 0)}</span>
        </div>
      </Card>

      {/* Card de Enrollments */}
      <Card className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={`${styles.cardIcon} ${styles.iconEnrollments}`}>
            <PersonSwap20Regular />
          </div>
          <span className={styles.cardTitle}>Enrollments</span>
        </div>
        <div className={styles.statsRows}>
          {/* Student Enrollment */}
          <div className={styles.statRow}>
            <span className={`${styles.statIcon} ${styles.statIconNew}`}><Add16Regular /></span>
            <span className={styles.statLabel}>Aluno</span>
            <div className={styles.statValues}>
              <span className={styles.statTotal}>{formatNumber((operations?.enrollments?.studentDetail?.success ?? 0) + (operations?.enrollments?.studentDetail?.failed ?? 0))}</span>
              <Tooltip content={`Sucesso: ${operations?.enrollments?.studentDetail?.success ?? 0} | Falha: ${operations?.enrollments?.studentDetail?.failed ?? 0}`} relationship="label">
                <span className={styles.statBreakdown}>
                  <span className={styles.successCount}><Checkmark12Regular />{operations?.enrollments?.studentDetail?.success ?? 0}</span>
                  <span className={styles.separator}>/</span>
                  <span className={styles.failedCount}><Dismiss12Regular />{operations?.enrollments?.studentDetail?.failed ?? 0}</span>
                </span>
              </Tooltip>
            </div>
          </div>
          {/* Teacher Enrollment */}
          <div className={styles.statRow}>
            <span className={`${styles.statIcon} ${styles.statIconNew}`}><Add16Regular /></span>
            <span className={styles.statLabel}>Prof.</span>
            <div className={styles.statValues}>
              <span className={styles.statTotal}>{formatNumber((operations?.enrollments?.teacherDetail?.success ?? 0) + (operations?.enrollments?.teacherDetail?.failed ?? 0))}</span>
              <Tooltip content={`Sucesso: ${operations?.enrollments?.teacherDetail?.success ?? 0} | Falha: ${operations?.enrollments?.teacherDetail?.failed ?? 0}`} relationship="label">
                <span className={styles.statBreakdown}>
                  <span className={styles.successCount}><Checkmark12Regular />{operations?.enrollments?.teacherDetail?.success ?? 0}</span>
                  <span className={styles.separator}>/</span>
                  <span className={styles.failedCount}><Dismiss12Regular />{operations?.enrollments?.teacherDetail?.failed ?? 0}</span>
                </span>
              </Tooltip>
            </div>
          </div>
          {/* Staff Enrollment */}
          <div className={styles.statRow}>
            <span className={`${styles.statIcon} ${styles.statIconNew}`}><Add16Regular /></span>
            <span className={styles.statLabel}>Staff</span>
            <div className={styles.statValues}>
              <span className={styles.statTotal}>{formatNumber((operations?.enrollments?.staffDetail?.success ?? 0) + (operations?.enrollments?.staffDetail?.failed ?? 0))}</span>
              <Tooltip content={`Sucesso: ${operations?.enrollments?.staffDetail?.success ?? 0} | Falha: ${operations?.enrollments?.staffDetail?.failed ?? 0}`} relationship="label">
                <span className={styles.statBreakdown}>
                  <span className={styles.successCount}><Checkmark12Regular />{operations?.enrollments?.staffDetail?.success ?? 0}</span>
                  <span className={styles.separator}>/</span>
                  <span className={styles.failedCount}><Dismiss12Regular />{operations?.enrollments?.staffDetail?.failed ?? 0}</span>
                </span>
              </Tooltip>
            </div>
          </div>
          {/* Sucesso */}
          <div className={styles.statRow}>
            <span className={`${styles.statIcon} ${styles.statIconSuccess}`}><CheckmarkCircle16Regular /></span>
            <span className={styles.statLabel}>Sucesso</span>
            <div className={styles.statValues}>
              <span className={styles.statTotal}>{formatNumber((operations?.enrollments?.studentDetail?.success ?? 0) + (operations?.enrollments?.teacherDetail?.success ?? 0) + (operations?.enrollments?.staffDetail?.success ?? 0))}</span>
            </div>
          </div>
          {/* Erros */}
          <div className={styles.statRow}>
            <span className={`${styles.statIcon} ${styles.statIconError}`}><ErrorCircle16Regular /></span>
            <span className={styles.statLabel}>Erros</span>
            <div className={styles.statValues}>
              <span className={styles.statTotal}>{formatNumber((operations?.enrollments?.studentDetail?.failed ?? 0) + (operations?.enrollments?.teacherDetail?.failed ?? 0) + (operations?.enrollments?.staffDetail?.failed ?? 0))}</span>
            </div>
          </div>
        </div>
        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>Total</span>
          <span className={styles.totalValue}>{formatNumber(operations?.enrollments?.total ?? 0)}</span>
        </div>
      </Card>
    </div>
  );
};

export default OperationMetricsCards;
